/**
 * The engineering floor: one room, three areas, everybody in it, live.
 *
 * Bugs, Features & SDD, and Others side by side. Each person stands in the area of what their agent
 * is doing now, so the room rearranges itself as work changes: somebody who picks up a bug walks
 * into the Bug Hunt's corner, somebody who opens a PRD moves to the specification boards, somebody
 * asking an agent questions with no ticket sits in Others — with a count of the prompts they sent,
 * because that is work too.
 *
 * Everybody's agents are in it, whatever they work on: each figure carries the project its agent is
 * on (from the agent's git remote), and so does each clone of a person running agents on two
 * projects at once. Choosing a project leaves only the agents working on it.
 *
 * A manager navigates it like a map: tabs to go to an area or see them all, zoom, drag the floor to
 * look around, drag a person to tidy a crowded area, star people to follow. Clicking a person says
 * what they are on and offers the specialist screen for it; a double click goes straight there.
 *
 * The same data as a list is one click away, for those who prefer a report to a room.
 *
 * All of that is the main hall. The office has two more areas, the kitchen and the war room
 * (docs/refactor/29, bughunt/office.ts): a person walks their own character there from its right-click
 * menu, and their agents stay at work in the hall, each a figure of its own — so the hall still shows
 * every prompt being run. A dropdown and an arrow at each side of the room go from area to area, and
 * on to the Bug Hunt. Every area grows with the crowd (layout.ts, roomHeightFor).
 */

import { RoomBox, useRoomBoxes } from '../bughunt/RoomBoxes'
import { useAgentLog } from '../bughunt/useAgentLog'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal,
  CalendarDays, Compass, Expand, FileText, Bug as BugIcon, LayoutGrid, List, ListChecks, Maximize2, MessageSquare, PanelBottom, RotateCcw, Star,
  Wrench, X, ZoomIn, ZoomOut, SlidersHorizontal, ChevronDown,
  FolderGit2, GitBranch,
} from 'lucide-react'
import { api } from '../lib/api'
import { useI18n, type Locale } from '../lib/i18n'
import { Dropdown, DropdownItem } from '../components/Dropdown'
import { realtime } from '../lib/realtime'
import { usePrefersReducedMotion } from '../lib/theme'
import { atLeast, getSession } from '../lib/auth'
import { formatDuration } from '../lib/format'
import { BugHuntStore } from '../bughunt/store'
import { cloneName, designHeightFor, DESIGN_WIDTH, type FloorSize } from '../bughunt/layout'
import { areaFromParam, awayAgents, hallHeightFor, roomAreaOf, routeFor, type Place } from '../bughunt/office'
import { OfficeArrows, OfficePicker, useWalkTo } from '../bughunt/OfficeNav'
import { OfficeArea } from '../bughunt/OfficeArea'
import { agentActivity, useSceneLabels } from '../bughunt/useSceneLabels'
import { Portrait, lookFor } from '../bughunt/Figures'
import { AREAS, areaOf, clampToArea, layoutRoom, overrideKey, primarySession, type AreaId } from '../room/areas'
import { RoomScene } from '../room/RoomScene'
import { BOARD_SLOTS } from '../room/Decor'
import { useCamera } from '../room/useCamera'
import { useRoomPrefs } from '../room/prefs'
import { Badge, ProgressBar } from '../components/primitives'
import { TeamPicker } from '../components/TeamPicker'
import { ProjectPicker } from '../components/ProjectPicker'
import { SceneryPicker } from '../components/SceneryPicker'
import { TeamChips } from '../components/TeamChips'
import { useBugControls } from '../bughunt/useBugControls'
import { combineControls, usePersonControls } from '../bughunt/usePersonControls'
import { useRoomChat } from '../bughunt/useRoomChat'
import { RoomChat } from '../components/RoomChat'
import { AgentCard, DeveloperFocus, SessionTabs } from '../bughunt/SessionPanels'
import { fromActivity } from '../bughunt/panelSession'
import { formatScope, useTeamFilter } from '../lib/teamFilter'
import { projectLabel, useProjectFilter } from '../lib/projectFilter'
import Live from './Live'
import type { ActivitySession, DeveloperActivity, RoomArea, SpecSummary, TeamRef, WorkCategory } from '../lib/types'

/** Whether the panels under the room are shown, per browser. */
const PANELS_KEY = 'devpulse:room-panels'

export default function Floor() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const reducedMotion = usePrefersReducedMotion()
  const session = getSession()
  const labels = useSceneLabels()

  const prefs = useRoomPrefs(session?.organizationId ?? 'default')
  const [onlyMarked, setOnlyMarked] = useState(false)

  // The room over the whole window, without the app's menus. Escape or the X leaves — but an
  // Escape that closes an open menu or dialog is that menu's, not a request to leave as well.
  const [fullscreen, setFullscreen] = useState(false)
  useEffect(() => {
    if (!fullscreen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('[role="menu"], [role="dialog"]')) return
      setFullscreen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen])
  const [showPanels, setShowPanels] = useState(() => {
    try {
      return localStorage.getItem(PANELS_KEY) !== 'hidden'
    } catch {
      return true
    }
  })
  const togglePanels = useCallback(() => {
    setShowPanels((current) => {
      try {
        localStorage.setItem(PANELS_KEY, current ? 'hidden' : 'shown')
      } catch {
        // Remembering is a nicety.
      }
      return !current
    })
  }, [])
  const markedSet = useMemo(() => new Set(prefs.marked), [prefs.marked])

  // ------------------------------------------------------------------ data

  const storeRef = useRef<BugHuntStore>()
  if (!storeRef.current) storeRef.current = new BugHuntStore()
  const store = storeRef.current
  const game = useSyncExternalStore(store.subscribe, store.snapshot)

  // A manager looks at everybody, a team, a squad or one person.
  const team = useTeamFilter()
  const { teamId, developerId } = team
  const scopeKey = formatScope(team.scope) || 'all'

  // And at every project, or one: with one chosen, only the agents working on it are in the room.
  const projectFilter = useProjectFilter()
  const { projectId } = projectFilter
  const projectKey = projectId ?? 'all'
  const viewKey = `${scopeKey}|${projectKey}`
  const scoped = scopeKey !== 'all' || projectId !== undefined
  const ready = team.ready && projectFilter.ready

  const { data: state } = useQuery({
    queryKey: ['bug-hunt', 'state', scopeKey, projectKey],
    queryFn: () => api.bugHunt.state({ teamId, developerId, projectId }),
    enabled: ready,
  })
  useEffect(() => {
    if (state) store.hydrate(state)
  }, [state, store])

  const { data: activity } = useQuery({
    queryKey: ['room-activity', scopeKey, projectKey],
    queryFn: () => api.activity.get({ everyone: true, teamId, developerId, projectId }),
    enabled: ready,
    refetchInterval: 10_000,
  })

  const { data: specs } = useQuery({ queryKey: ['specs', {}], queryFn: () => api.specs.list(), refetchInterval: 30_000 })

  // Live: bug events animate the room as they arrive; session updates refresh who is where.
  useEffect(
    () =>
      realtime.onBugEvents((events) => {
        store.enqueue(scoped ? events.filter((e) => store.concerns(e)) : events)
        if (events.some((e) => e.kind !== 'BugProgressChanged' && e.kind !== 'DeveloperWorkStateChanged')) {
          void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
        }
      }),
    [store, queryClient, scoped],
  )
  useEffect(() => realtime.onSessions(() => void queryClient.invalidateQueries({ queryKey: ['room-activity'] })), [queryClient])

  const activityById = useMemo(() => new Map((activity ?? []).map((d) => [d.developerId, d])), [activity])

  // --------------------------------------------------------------- layout

  const stageRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [size, setSize] = useState<FloorSize>({ width: DESIGN_WIDTH, height: 640 })

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const measure = () => {
      const rect = stage.getBoundingClientRect()
      if (rect.width === 0) return
      const height = designHeightFor(rect.width, rect.height)
      setSize((current) => (current.height === height ? current : { width: DESIGN_WIDTH, height }))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(stage)
    return () => observer.disconnect()
  }, [prefs.view])

  // Where somebody is being dragged right now, before it is saved.
  const [dragging, setDragging] = useState<{ key: string; x: number; y: number } | null>(null)

  const developers = useMemo(
    () => [...game.developers.values()].filter((d) => !onlyMarked || prefs.marked.includes(d.id)),
    [game.developers, onlyMarked, prefs.marked],
  )

  // ---------------------------------------------------------------- areas

  // Which area of the office is in view, in the address so a link or the Bug Hunt's arrows open it.
  const [params, setParams] = useSearchParams()
  // The showcase (41 §4.6): ?overlay=1 shows the scene alone — no toolbar, panels or chat — for OBS or a projector.
  const overlay = params.get('overlay') === '1'
  useEffect(() => {
    if (!overlay) return
    setFullscreen(true)
    prefs.setView('room')
    const leave = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const next = new URLSearchParams(params)
      next.delete('overlay')
      setParams(next, { replace: true })
      setFullscreen(false)
    }
    window.addEventListener('keydown', leave)
    return () => window.removeEventListener('keydown', leave)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay])
  const place = areaFromParam(params.get('area'))
  const goPlace = useCallback(
    (next: Place) => {
      if (next === 'bugs') {
        navigate(routeFor('bugs'))
        return
      }
      setParams((current) => {
        const copy = new URLSearchParams(current)
        if (next === 'hall') copy.delete('area')
        else copy.set('area', next)
        return copy
      }, { replace: true })
    },
    [navigate, setParams],
  )

  // Who walked out of the hall, by area; and their agents at work, which stay in it as figures of
  // their own (bughunt/office.ts).
  const { hallPeople, awayByArea, away, ownerOfFigure } = useMemo(() => {
    const hallPeople: typeof developers = []
    const awayByArea: Record<Exclude<RoomArea, 'hall'>, typeof developers> = { kitchen: [], 'war-room': [] }
    for (const developer of developers) {
      const area = roomAreaOf(activityById.get(developer.id))
      if (area === 'hall') hallPeople.push(developer)
      else awayByArea[area].push(developer)
    }
    const away = awayAgents(
      [...awayByArea.kitchen, ...awayByArea['war-room']],
      (id) => activityById.get(id),
      (owner, index) => (index === 0 ? t('office.agentOf', { name: owner }) : cloneName(index + 1, owner)),
    )
    return { hallPeople, awayByArea, away, ownerOfFigure: new Map(away.map((agent) => [agent.entity.id, agent.ownerId])) }
  }, [developers, activityById, t])

  /** Everybody standing in the hall: its people, and the agents of those who are elsewhere. */
  const hallFigures = useMemo(() => [...hallPeople, ...away.map((agent) => agent.entity)], [hallPeople, away])
  const ownerOf = useCallback((id: string) => ownerOfFigure.get(id) ?? id, [ownerOfFigure])

  const areaById = useMemo(() => {
    const map = new Map<string, AreaId>()
    for (const developer of hallPeople) map.set(developer.id, areaOf(activityById.get(developer.id)))
    // An agent stands in the zone of what it is working on, like a person would.
    for (const agent of away) map.set(agent.entity.id, areaOf({ sessions: agent.activity ? [agent.activity] : [] } as unknown as DeveloperActivity))
    return map
  }, [hallPeople, away, activityById])

  // The hall grows with the crowd in its busiest zone (layout.ts, roomHeightFor); the camera's "fit"
  // shows all of it, and zooming in gets close to anybody.
  const hallSize = useMemo<FloorSize>(() => {
    const figures: Record<AreaId, number> = { bugs: 0, features: 0, others: 0 }
    for (const area of areaById.values()) figures[area]++
    return { width: size.width, height: hallHeightFor(figures, size) }
  }, [areaById, size])

  const camera = useCamera(hallSize.height, reducedMotion)

  const placed = useMemo(() => {
    const overrides = dragging ? { ...prefs.overrides, [dragging.key]: { x: dragging.x, y: dragging.y } } : prefs.overrides
    return layoutRoom(hallFigures.map((d) => ({ id: d.id, area: areaById.get(d.id)! })), hallSize, overrides)
  }, [hallFigures, areaById, hallSize, prefs.overrides, dragging])

  const onDrag = useCallback(
    (developerId: string, area: AreaId, x: number, y: number, done: boolean) => {
      const spot = clampToArea(area, x, y, hallSize)
      const key = overrideKey(developerId, area)
      if (done) {
        prefs.setOverrides((current) => ({ ...current, [key]: spot }))
        setDragging(null)
      } else {
        setDragging({ key, ...spot })
      }
    },
    [prefs, hallSize],
  )

  // A bug an away person's agent is on stays with that agent in the hall.
  const bugs = useMemo(() => {
    const visible = new Set(developers.map((d) => d.id))
    return [...game.bugs.values()]
      .filter((bug) => !bug.developerId || visible.has(bug.developerId) || !onlyMarked)
      .map((bug) => (bug.workSessionId && ownerOfFigure.has(bug.workSessionId) ? { ...bug, developerId: bug.workSessionId } : bug))
  }, [game.bugs, developers, onlyMarked, ownerOfFigure])

  // What each card says, the prompt bubble beside it, and — with every project shown — the project
  // over it. The card's session is the person; their other busy sessions are the clones beside them.
  const { details, prompts, projects, primaries } = useMemo(() => {
    const details = new Map<string, string | null>()
    const prompts = new Map<string, number>()
    const projects = new Map<string, string | null>()
    const primaries = new Map<string, string>()
    const describeOn = (id: string, primary: ActivitySession | undefined) => {
      details.set(id, primary ? describe(primary, t) : null)
      if (primary && primary.promptCount > 0) prompts.set(id, primary.promptCount)
      if (primary) primaries.set(id, primary.workSessionId)
      projects.set(id, projectLabel(primary))
    }
    for (const developer of hallPeople) describeOn(developer.id, primarySession(activityById.get(developer.id)))
    for (const agent of away) describeOn(agent.entity.id, agent.activity)
    return { details, prompts, projects: projectId ? undefined : projects, primaries }
  }, [hallPeople, away, activityById, t, projectId])

  // The boards on the Features wall: what people are on now first, then what is in progress.
  const boards = useMemo(() => pickBoards(specs?.specs ?? []), [specs])

  // People, not figures: an agent standing in for somebody is not one more person in the zone.
  const counts = useMemo(() => {
    const result: Record<AreaId, number> = { bugs: 0, features: 0, others: 0 }
    for (const developer of hallPeople) result[areaById.get(developer.id)!]++
    return result
  }, [hallPeople, areaById])

  // The war room's screen: the agents at work in the whole office right now. Nobody singled out.
  const agentsAtWork = useMemo(
    () => developers.reduce((sum, d) => sum + d.sessions.filter((s) => s.activityState !== 'Idle').length, 0),
    [developers],
  )

  const areaTitles: Record<AreaId, string> = { bugs: t('room.area.bugs'), features: t('room.area.features'), others: t('room.area.others') }
  const areaPeople: Record<AreaId, string> = {
    bugs: t('room.people', { count: counts.bugs }),
    features: t('room.people', { count: counts.features }),
    others: t('room.people', { count: counts.others }),
  }

  // ------------------------------------------------------------- selection

  const selected = game.selectedDeveloperId ? game.developers.get(game.selectedDeveloperId) : undefined
  const selectedActivity = selected ? activityById.get(selected.id) : undefined

  // The panels under the room: whoever was clicked, or else the first person with an agent
  // running — so they are never empty while something is happening.
  const focus = useMemo(() => {
    if (selected) return selected
    const withSession = developers.filter((d) => primarySession(activityById.get(d.id)))
    return (
      withSession.find((d) => primarySession(activityById.get(d.id))?.activityState !== 'Idle') ??
      withSession[0] ??
      developers[0]
    )
  }, [selected, developers, activityById])

  const focusPrimary = focus ? primarySession(activityById.get(focus.id)) : undefined

  // The three boxes under the room, each foldable, and all three at once (owner, 08/10/2026).
  const boxes = useRoomBoxes()
  // Each agent has its own log (bughunt/useAgentLog.tsx): a click on an agent picks it, the person shows them all.
  const agentLog = useAgentLog({
    personId: focus?.id,
    sessions: focus ? activityById.get(focus.id)?.sessions ?? [] : [],
    agentLooks: focus?.agentLooks,
    enabled: prefs.view === 'room' && showPanels,
  })
  const pickedSession = agentLog.picked
  const panelSession = useMemo(
    () => (pickedSession ? fromActivity(pickedSession) : focusPrimary ? fromActivity(focusPrimary) : undefined),
    [pickedSession, focusPrimary],
  )

  // Files, tokens and cost change with every event, so the detail is polled like the Bug Hunt's.
  const { data: focusDetail } = useQuery({
    queryKey: ['work', panelSession?.workSessionId],
    queryFn: () => api.sessions.detail(panelSession!.workSessionId),
    enabled: Boolean(panelSession?.workSessionId) && prefs.view === 'room' && showPanels,
    refetchInterval: 10_000,
  })
  const shownDetail = useMemo(() => agentLog.merge(focusDetail), [agentLog, focusDetail])

  // A click on an agent standing in for somebody selects that somebody.
  const selectDeveloper = useCallback((id: string | null) => store.select(id ? ownerOf(id) : null, null), [store, ownerOf])
  const selectBug = useCallback(
    (taskId: string | null) => {
      const bug = taskId ? store.snapshot().bugs.get(taskId) : null
      store.select(bug?.developerId ?? null, taskId)
    },
    [store],
  )

  /** The specialist screen for what somebody is doing: the bug, the specification, or their day. */
  const openSpecialist = useCallback(
    (figureId: string) => {
      const developerId = ownerOf(figureId)
      const primary = primarySession(activityById.get(developerId))
      if (primary?.category === 'Bug') navigate('/track/bugs')
      else if (primary?.specificationId) navigate(`/project/specs?spec=${primary.specificationId}`)
      else navigate(`/developers/${developerId}/day`)
    },
    [activityById, navigate, ownerOf],
  )

  const [activeArea, setActiveArea] = useState<AreaId | 'all'>('bugs')
  const { overview, focusArea } = camera
  const goTo = useCallback(
    (area: AreaId | 'all') => {
      setActiveArea(area)
      if (area === 'all') overview()
      else focusArea(AREAS.indexOf(area))
    },
    [overview, focusArea],
  )

  // Right-click a mosquito for its menu; drag it to the jar or to a person. For those who may move bugs.
  const bugControls = useBugControls({
    store,
    enabled: atLeast(session?.role, 'Developer'),
    onOpenBug: (taskId) => store.select(store.snapshot().bugs.get(taskId)?.developerId ?? null, taskId),
  })

  // Speak in the room from your own character; right-click anybody for their menu.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const chat = useRoomChat()

  // Right-click your own character, "Ir para…": it walks there, and the view goes with it.
  const myArea = me?.developerId ? roomAreaOf(activityById.get(me.developerId)) : undefined
  const moveMe = useWalkTo(goPlace)

  const personControls = usePersonControls({
    meDeveloperId: me?.developerId,
    nameOf: (id) => store.snapshot().developers.get(id)?.name ?? '',
    onSay: chat.say,
    ownerOf: (id) => ownerOfFigure.get(id),
    moveTo: { current: myArea, go: moveMe },
  })
  const handlers = combineControls(bugControls.handlers, personControls.handlers)

  // Choosing one person takes the camera to their area; choosing a team with nobody where the
  // camera is shows the whole room. Once per choice, after its data arrives — never fighting a
  // manager who then looks elsewhere.
  const framedScope = useRef<string>('all|all')
  useEffect(() => {
    if (framedScope.current === viewKey || !activity) return
    if (developerId) {
      const area = areaById.get(developerId)
      if (!area) return
      framedScope.current = viewKey
      goTo(area)
      return
    }
    framedScope.current = viewKey
    if (activeArea !== 'all' && counts[activeArea] === 0 && AREAS.some((area) => counts[area] > 0)) goTo('all')
  }, [viewKey, developerId, activity, areaById, counts, activeArea, goTo])

  // -------------------------------------------------------------- render

  return (
    <div
      className={`floor theme-night${prefs.view === 'room' && showPanels && !overlay ? ' floor--panels' : ''}${fullscreen ? ' floor--fullscreen' : ''}${overlay ? ' floor--overlay' : ''}`}
      {...handlers}
      onClickCapture={(event) => {
        // A click on an agent in the room: the panels show that agent's log (its person is selected as usual).
        agentLog.pickFromClick(event)
        handlers.onClickCapture(event)
      }}
    >
      {bugControls.overlay}
      {personControls.overlay}
      <div className="floor__toolbar">
        {prefs.view === 'room' && <OfficePicker current={place} onGo={goPlace} />}

        {/* One row (51, D2): who, which project and which zone of the hall, in one "Mostrar" menu. */}
        {(team.show || projectFilter.show || place === 'hall' || prefs.view === 'report') && (
          <Dropdown
            label={t('room.show')}
            buttonClassName={`btn btn--ghost btn--sm floor__show${team.scope || projectFilter.projectId || activeArea !== 'all' ? ' is-active' : ''}`}
            button={
              <>
                <SlidersHorizontal size={13} aria-hidden />
                <span className="truncate">
                  {[
                    activeArea !== 'all' && prefs.view === 'room' ? areaTitles[activeArea] : null,
                    projectFilter.project?.name ?? null,
                    team.scope ? t('room.showTeam') : null,
                  ].filter(Boolean).join(' · ') || t('room.showAll')}
                </span>
                <ChevronDown size={12} aria-hidden />
              </>
            }
          >
            {(close) => (
              <div className="floor__show-menu stack stack--tight">
                <TeamPicker filter={team} />
                <ProjectPicker filter={projectFilter} />
                {/* The hall's zones; the kitchen and the war room are one floor each. */}
                {(place === 'hall' || prefs.view === 'report') && (
                  <>
                    <div className="dropdown__sep" role="separator" />
                    {(['all', ...AREAS] as const).map((area) => (
                      <DropdownItem
                        key={area}
                        label={area === 'all' ? t('room.overview') : areaTitles[area]}
                        side={area === 'all' ? (activeArea === 'all' ? '✓' : undefined) : `${counts[area]}${activeArea === area ? ' ✓' : ''}`}
                        onSelect={() => {
                          prefs.setView('room')
                          goTo(area)
                          close()
                        }}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </Dropdown>
        )}

        <div className="spacer" />

        {prefs.view === 'room' && (
          <div className="row row--tight">
            {!fullscreen && (
              <button type="button" className="bughunt__icon-button" onClick={() => setFullscreen(true)} aria-label={t('room.fullscreen')} title={t('room.fullscreen')}>
                <Expand size={14} />
              </button>
            )}
            <button
              type="button"
              className={`btn btn--sm ${showPanels ? 'btn--primary' : 'btn--ghost'}`}
              onClick={togglePanels}
              aria-pressed={showPanels}
              title={t('room.panelsHint')}
            >
              <PanelBottom size={13} /> {t('room.panels')}
            </button>
            {/* Zoom, fit, marked, reorganize and the scenery: useful sometimes, a wall every day (41, A10).
                The canvas itself already zooms with the wheel and pans by dragging. */}
            <Dropdown label={t('room.more')} buttonClassName="bughunt__icon-button" button={<MoreHorizontal size={14} />}>
              {(close) => (
                <div className="floor__more stack">
                  {place === 'hall' && (
                    <div className="row row--tight floor__more-zoom">
                      <button type="button" className="bughunt__icon-button" onClick={() => camera.zoomBy(1 / 1.25)} aria-label={t('room.zoomOut')} title={t('room.zoomOut')}>
                        <ZoomOut size={14} />
                      </button>
                      <span className="tiny mono floor__zoom">{Math.round(camera.camera.zoom * 100)}%</span>
                      <button type="button" className="bughunt__icon-button" onClick={() => camera.zoomBy(1.25)} aria-label={t('room.zoomIn')} title={t('room.zoomIn')}>
                        <ZoomIn size={14} />
                      </button>
                      <button type="button" className="bughunt__icon-button" onClick={() => goTo(activeArea)} aria-label={t('room.fit')} title={t('room.fit')}>
                        <Maximize2 size={14} />
                      </button>
                    </div>
                  )}
                  <DropdownItem
                    icon={<Star size={13} />}
                    label={t('room.onlyMarked', { count: prefs.marked.length })}
                    side={onlyMarked ? t('common.on') : undefined}
                    disabled={prefs.marked.length === 0}
                    onSelect={() => { setOnlyMarked((value) => !value); close() }}
                  />
                  {place === 'hall' && (
                    <DropdownItem
                      icon={<RotateCcw size={13} />}
                      label={t('room.resetLayout')}
                      hint={t('room.resetLayoutHint')}
                      disabled={Object.keys(prefs.overrides).length === 0}
                      onSelect={() => { prefs.setOverrides({}); close() }}
                    />
                  )}
                  <DropdownItem
                    icon={<Expand size={13} />}
                    label={t('room.overlayMode')}
                    hint={t('room.overlayHint')}
                    onSelect={() => {
                      const next = new URLSearchParams(params)
                      next.set('overlay', '1')
                      setParams(next)
                    }}
                  />
                  <div className="floor__more-scenery"><SceneryPicker /></div>
                </div>
              )}
            </Dropdown>
          </div>
        )}

        <div className="segmented">
          <button type="button" className={`segmented__item${prefs.view === 'room' ? ' segmented__item--active' : ''}`} onClick={() => prefs.setView('room')}>
            <LayoutGrid size={13} /> {t('room.viewRoom')}
          </button>
          <button type="button" className={`segmented__item${prefs.view === 'report' ? ' segmented__item--active' : ''}`} onClick={() => prefs.setView('report')}>
            <List size={13} /> {t('room.viewReport')}
          </button>
        </div>

        {fullscreen && (
          <button
            type="button"
            className="bughunt__icon-button floor__exit-fullscreen"
            onClick={() => setFullscreen(false)}
            aria-label={t('room.exitFullscreen')}
            title={t('room.exitFullscreen')}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {prefs.view === 'report' ? (
        <div className="floor__report">
          <Live projectId={projectId} />
        </div>
      ) : (
        <div className="floor__stage" ref={stageRef}>
          {place === 'hall' ? (
          <RoomScene
            svgRef={svgRef}
            size={hallSize}
            viewBox={camera.viewBox}
            labels={labels}
            developers={hallFigures}
            placed={placed}
            bugs={bugs}
            boards={boards}
            areaTitles={areaTitles}
            areaPeople={areaPeople}
            selectedDeveloperId={game.selectedDeveloperId}
            selectedBugId={game.selectedBugId}
            marked={markedSet}
            prompts={prompts}
            details={details}
            projects={projects}
            projectOf={projectLabel}
            primaries={primaries}
            reducedMotion={reducedMotion}
            onSelectDeveloper={selectDeveloper}
            onSelectBug={selectBug}
            onOpenSpecialist={openSpecialist}
            onOpenSpec={(id) => navigate(`/project/specs?spec=${id}`)}
            onOpenJar={() => navigate('/track/bugs')}
            onDrag={onDrag}
            onPan={camera.panBy}
            onZoom={camera.zoomBy}
            speech={chat.speech}
            speechScale={Math.min(2.5, Math.max(1, 1 / camera.camera.zoom))}
          />
          ) : (
            <OfficeArea
              area={place}
              size={size}
              people={awayByArea[place]}
              title={t(place === 'kitchen' ? 'office.kitchen' : 'office.warRoom')}
              peopleLabel={t('room.people', { count: awayByArea[place].length })}
              emptyHint={t('office.empty')}
              screen={place === 'war-room' ? { value: agentsAtWork, label: t('office.agentsAtWork', { count: agentsAtWork }) } : undefined}
              selectedDeveloperId={game.selectedDeveloperId}
              speech={chat.speech}
              reducedMotion={reducedMotion}
              onSelect={selectDeveloper}
            />
          )}

          <OfficeArrows current={place} onGo={goPlace} />

          {place === 'hall' && <p className="floor__hint tiny" title={t('room.hint')}>{t('room.hint')}</p>}

          {!overlay && <RoomChat chat={chat} meDeveloperId={me?.developerId} />}

          {selected && (
            <PersonPanel
              name={selected.name}
              developerId={selected.id}
              avatar={selected.avatar}
              team={selected.teamName}
              teams={selectedActivity?.teams ?? selected.teams}
              onPickTeam={team.show ? (id) => team.setScope({ type: 'team', id }) : undefined}
              activity={selectedActivity}
              marked={prefs.marked.includes(selected.id)}
              onToggleMark={() => prefs.toggleMark(selected.id)}
              onClose={() => store.select(null, null)}
              onOpenSpecialist={() => openSpecialist(selected.id)}
              locale={locale}
            />
          )}
        </div>
      )}

      {prefs.view === 'room' && showPanels && !overlay && (
        <section className={`bughunt__bottom floor__bottom${boxes.allClosed ? ' room-boxes--folded' : ''}`} aria-label={t('room.panels')}>
          <RoomBox id="person" title={t('room.boxes.person')} boxes={boxes}>
          <div className="bh-panel bh-focus">
            {focus ? (
              <DeveloperFocus
                person={{ id: focus.id, name: focus.name, avatar: focus.avatar, teams: activityById.get(focus.id)?.teams ?? focus.teams }}
                working={Boolean(focusPrimary) && focusPrimary!.activityState !== 'Idle'}
                session={panelSession}
                detail={focusDetail}
                lead={panelSession?.category ? <CategoryIcon category={panelSession.category} /> : undefined}
              />
            ) : (
              <span className="tiny muted">{t('bugHunt.selectDeveloper')}</span>
            )}
          </div>
          </RoomBox>

          <RoomBox id="log" title={t('room.boxes.log')} boxes={boxes}>
          <div className="bh-panel">
            {/* Whose log: all of the person's agents together, or one agent's (a click on it in the room picks it). */}
            {agentLog.switcher}
            {/* The room's live stream is about bugs; the session's own timeline is what fits here. */}
            <SessionTabs key={`${panelSession?.workSessionId ?? 'none'}${agentLog.all ? ':all' : ''}`} session={panelSession} detail={shownDetail} log={[]} />
          </div>

          </RoomBox>

          <RoomBox id="agent" title={t('room.boxes.agent')} boxes={boxes}>
            <AgentCard session={panelSession} detail={focusDetail} />
          </RoomBox>
        </section>
      )}
    </div>
  )
}

/** The kind of work, as a small mark before the ticket: never a mosquito — the room is not the Bug Hunt. */
function CategoryIcon({ category }: { category: WorkCategory }) {
  const Icon = category === 'Spec' ? FileText : category === 'Task' ? ListChecks : category === 'Bug' ? Wrench : Compass
  return <Icon size={15} className="floor__category" aria-hidden />
}

/** A few words for the card: the ticket or specification, and what the agent is doing on it. */
function describe(session: ActivitySession, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const reference =
    session.taskExternalId ??
    (session.specificationKey ? `${session.specificationKind === 'Prd' ? 'PRD' : 'SDD'} ${session.specificationKey}` : null) ??
    (session.subject ? session.subject : t('room.exploring'))

  const short = reference.length > 14 ? `${reference.slice(0, 13)}…` : reference
  return `${short} · ${agentActivity(session.activityState, t)}`
}

/** Specifications worth a board: those people are on now, then those in progress, most recent first. */
function pickBoards(specs: SpecSummary[]): SpecSummary[] {
  const working = (s: SpecSummary) => s.liveSessions.filter((l) => l.activityState !== 'Idle').length

  return specs
    .filter((s) => s.status !== 'Done')
    .sort((a, b) =>
      working(b) - working(a)
      || (a.status === 'InProgress' ? 0 : 1) - (b.status === 'InProgress' ? 0 : 1)
      || (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''))
    .slice(0, BOARD_SLOTS)
}

/** Who was clicked: what they are on, and the ways to look closer. */
function PersonPanel({
  name,
  developerId,
  avatar,
  team,
  teams,
  onPickTeam,
  activity,
  marked,
  onToggleMark,
  onClose,
  onOpenSpecialist,
  locale,
}: {
  name: string
  developerId: string
  avatar: DeveloperActivity['avatar']
  team: string | null
  /** Every team and squad they are in. */
  teams: TeamRef[]
  /** Switches the room to that team, for a manager. */
  onPickTeam?: (teamId: string) => void
  activity: DeveloperActivity | undefined
  marked: boolean
  onToggleMark: () => void
  onClose: () => void
  onOpenSpecialist: () => void
  locale: Locale
}) {
  const { t } = useI18n()
  const primary = primarySession(activity)
  const look = lookFor(developerId, avatar)

  return (
    <aside className="floor__panel" aria-label={name}>
      <div className="row row--tight">
        <Portrait look={look} size={40} />
        <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}>
          <span className="bold truncate">{name}</span>
          {teams.length > 0
            ? <TeamChips teams={teams} onPick={onPickTeam} />
            : <span className="tiny muted">{team ?? t('common.unassigned')}</span>}
        </div>
        <button type="button" className={`bughunt__icon-button${marked ? ' floor__star--on' : ''}`} onClick={onToggleMark} aria-pressed={marked} title={marked ? t('room.unmark') : t('room.mark')}>
          <Star size={14} />
        </button>
        <button type="button" className="bughunt__icon-button" onClick={onClose} aria-label={t('common.close')}>
          <X size={14} />
        </button>
      </div>

      {!primary ? (
        <span className="small muted">{t('activity.noAgent')}</span>
      ) : (
        <div className="stack stack--tight">
          <div className="row row--tight row--wrap">
            <Badge tone={primary.category === 'Bug' ? 'danger' : primary.category === 'Spec' ? 'info' : primary.category === 'Task' ? 'accent' : 'neutral'}>
              {primary.category === 'Spec' && primary.specificationKind
                ? (primary.specificationKind === 'Prd' ? 'PRD' : 'SDD')
                : t(`day.category.${primary.category}`)}
            </Badge>
            {primary.taskExternalId && <span className="mono small bold">{primary.taskExternalId}</span>}
            {primary.specificationKey && primary.category !== 'Spec' && <Badge tone="info">{primary.specificationKey}</Badge>}
          </div>
          <span className="small">{primary.subject ?? t('activity.noSubject')}</span>
          {primary.kind === 'TaskWork' && <ProgressBar value={primary.progressPercent} showValue size="sm" />}
          {(primary.currentStepNote || primary.currentStepTitle) && (
            <span className="tiny muted">{primary.currentStepNote ?? primary.currentStepTitle}</span>
          )}
          <div className="row row--wrap tiny muted">
            <span>{agentActivity(primary.activityState, t)}</span>
            <span>{formatDuration(primary.durationSeconds, locale)}</span>
            {(primary.projectName || primary.projectKey) && (
              <span className="row row--tight" title={t('projectPicker.label')}>
                <FolderGit2 size={11} aria-hidden /> <span>{primary.projectName ?? primary.projectKey}</span>
              </span>
            )}
            {primary.repositoryName && <span className="mono">{primary.repositoryName}</span>}
            {primary.repositoryName && (
              <span className="row row--tight" title={t('branch.agentOn')}>
                <GitBranch size={11} /> <span className="mono">{primary.branch ?? t('branch.unknown')}</span>
              </span>
            )}
            {primary.promptCount > 0 && (
              <span><MessageSquare size={11} /> {t('day.promptsCount', { count: primary.promptCount })}</span>
            )}
          </div>
        </div>
      )}

      <div className="stack stack--tight">
        {primary?.category === 'Bug' && (
          <button type="button" className="btn btn--sm btn--primary" onClick={onOpenSpecialist}>
            <BugIcon size={13} /> {t('room.openBugHunt')}
          </button>
        )}
        {primary?.specificationId && primary.category !== 'Bug' && (
          <button type="button" className="btn btn--sm btn--primary" onClick={onOpenSpecialist}>
            <FileText size={13} /> {t('day.openSpec')}
          </button>
        )}
        <Link to={`/developers/${developerId}/day`} className="btn btn--sm">
          <CalendarDays size={13} /> {t('day.open')}
        </Link>
        {primary && (
          <Link to={`/work/${primary.workSessionId}`} className="btn btn--sm btn--ghost">
            {t('live.viewSession')}
          </Link>
        )}
      </div>
    </aside>
  )
}
